import { config } from '@iheartjane/config';
import { sendMfaCode, verifyMfaCode } from '@iheartjane/data-access';
import {
  useAeropayBankConnection,
  useAeropayUser,
  useLinkAeropayBankAccount,
} from '@iheartjane/data-access/react-query';
import {
  FailureScreen,
  MfaProvider,
  MoreInfoScreen,
  WIDGET_CONTAINER_ID,
  useAerosync,
  useMfa,
} from '@iheartjane/menu';
import { Box, Loading, useWindowListener } from '@iheartjane/reefer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import {
  AeropayScreen,
  BankLinkingSuccessScreen,
  SignedInWelcomeScreen,
  SuccessConfirmationScreen,
} from '@jane/kiosk/components';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import {
  SCREEN_NAMES,
  useLinkBankOnDeviceData,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import {
  trackAbandonedBankAccountLinking,
  trackBeginsBankAccountLinking,
  trackLinkedBankAccount,
} from '@jane/kiosk/util';
import { getJwt } from '@jane/shared/auth';
import { useJaneUser } from '@jane/shared/data-access';

const getStep = (screen: (typeof SCREEN_NAMES)[keyof typeof SCREEN_NAMES]) => {
  if (screen === 'mfa') return 'Verification';
  if (screen === 'more-info') return 'Information';
  if (screen === 'failure') return 'Failure';
  return 'Initial';
};

const LinkBankOnDeviceContent = () => {
  const { state } = useLocation();
  const jwt = getJwt();
  const { data: janeUser } = useJaneUser();
  const { setNewPhone } = useMfa();
  const hasSentTrackingEvent = useRef(false);
  const hasLinkedBank = useRef(false);
  const { store } = useTypedRouteLoaderData<RootLoaderType>('root');
  const storeHasWhiteLabel = Boolean(store?.jane_pay_integration?.white_label);
  const storeHasJaneGold = useShouldKioskShowGold();
  const [queryParams] = useSearchParams();
  const source = queryParams.get('source') || '';
  const [error, setError] = useState<unknown | null>(null);
  const {
    afterVerifySuccess,
    handleDoneWithOptOut,
    handleSubmitInfo,
    needsMoreFlag,
    pendingPhone,
    screen,
    setScreen,
  } = useLinkBankOnDeviceData({
    initialScreen: queryParams.get('step'),
    onSetNewPhone: setNewPhone,
    setError,
  });

  const handleError = (err: unknown) => {
    setError(err);
    setScreen(SCREEN_NAMES.failure);
  };

  const { data: aeropayUser } = useAeropayUser({
    janeUserId: janeUser?.user?.id,
    jwt,
  });
  const { data: aeropayBankConnection } = useAeropayBankConnection({
    aeropayUserId: aeropayUser?.user_id,
    jwt,
  });
  const { mutate: linkAeropayBankAccount } = useLinkAeropayBankAccount({
    janeUserId: janeUser?.user?.id,
    jwt,
  });

  const {
    isLoading: isLoadingWidget,
    linkingBankAccount,
    widgetRef,
  } = useAerosync({
    aeropayConsumerId: config.aeropayConsumerId,
    aeropayEnv: config.aeropayEnv,
    token: aeropayBankConnection?.token,
    onError: handleError,
    onLinkBankAccount: linkAeropayBankAccount,
    onSuccess: () => {
      trackLinkedBankAccount({
        deviceType: state?.fromMobile ? 'Mobile' : 'Kiosk',
        source: [source],
        store,
      });
      setScreen(SCREEN_NAMES.success);
    },
    widgetContainerId: WIDGET_CONTAINER_ID,
  });

  useEffect(() => {
    if (store && source && !hasSentTrackingEvent.current) {
      trackBeginsBankAccountLinking({
        deviceType: state?.fromMobile ? 'Mobile' : 'Kiosk',
        store,
        source: [source],
      });
      hasSentTrackingEvent.current = true;
    }
  }, [store, source]);

  /** @todo include "Verification" in abandoned tracking (figure out how to know we sent an mfa but it wasn't completed) */
  const handleBeforeUnload = useCallback(() => {
    if (!hasLinkedBank.current) {
      trackAbandonedBankAccountLinking({
        deviceType: state?.fromMobile ? 'Mobile' : 'Kiosk',
        step: getStep(screen),
        store,
        source: [source],
      });
    }
  }, [screen, source, store]);

  useWindowListener('beforeunload', handleBeforeUnload);

  const renderSwitch = () => {
    const {
      aeropay,
      failure,
      loading,
      moreInfo,
      success,
      successConfirmation,
      signedInWelcome,
    } = SCREEN_NAMES;

    // NOTE: When already signed in, we show the signedInWelcome screen. If not we start on the aeropay screen.
    // The flow is signedInWelcome -> aeropay -> moreInfo (if needed because of missing info) -> success -> successConfirmation (if jane gold then users can opt out and end up on this screen)
    switch (screen) {
      case aeropay:
        return (
          <AeropayScreen
            isLoadingWidget={isLoadingWidget}
            isLinkingBankAccount={linkingBankAccount}
            newPhone={pendingPhone}
            onVerified={afterVerifySuccess}
            widgetRef={widgetRef}
          />
        );
      case failure:
        return (
          <Box p={24}>
            <FailureScreen
              buttonSize="small"
              error={error}
              onDoneClick={() => setScreen(signedInWelcome)}
              onTryAgainClick={() => setScreen(aeropay)}
              tryAgainButtonVariant="tertiary"
            />
          </Box>
        );
      case loading:
        return (
          <Box py={48} position="relative">
            <Loading color="black" size="lg" />
          </Box>
        );
      case moreInfo:
        return (
          <Box p={24}>
            <MoreInfoScreen
              onSubmit={handleSubmitInfo}
              user={{
                firstName: janeUser?.user?.first_name || undefined,
                lastName: janeUser?.user?.last_name || undefined,
                phone: janeUser?.user?.phone || undefined,
              }}
            />
          </Box>
        );
      case signedInWelcome:
        return (
          <SignedInWelcomeScreen
            onLinkBankClick={() =>
              setScreen(
                needsMoreFlag ? SCREEN_NAMES.moreInfo : SCREEN_NAMES.aeropay
              )
            }
            storeHasJaneGold={storeHasJaneGold}
            storeHasWhiteLabel={storeHasWhiteLabel}
          />
        );
      case success:
        return (
          <BankLinkingSuccessScreen
            storeHasJaneGold={storeHasJaneGold}
            storeHasWhiteLabel={storeHasWhiteLabel}
            onDoneClick={handleDoneWithOptOut}
            storePhoto={store?.photo}
          />
        );
      case successConfirmation:
        return <SuccessConfirmationScreen />;
      default:
        return null;
    }
  };

  return <Box width="100%">{renderSwitch()}</Box>;
};

export const LinkBankOnDevice = () => {
  const { pathname } = useLocation();
  const isKiosk = !pathname.includes('mobile');
  const { data: janeUser } = useJaneUser();

  return (
    <MfaProvider
      getJwt={getJwt}
      isKiosk={isKiosk}
      sendMfaCode={sendMfaCode}
      userId={janeUser?.user?.id ?? null}
      verifyMfaCode={verifyMfaCode}
    >
      <LinkBankOnDeviceContent />
    </MfaProvider>
  );
};
